
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Edit, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";

interface PersonalInfoTabProps {
  userData: any;
}

export const PersonalInfoTab: React.FC<PersonalInfoTabProps> = ({ userData }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: userData?.name || '',
    phone: userData?.phone || ''
  });

  if (!userData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className={isMobile ? "text-lg" : ""}>{t("personal_information")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">{t("no_user_data")}</p>
            <p className="text-sm text-muted-foreground">
              Please refresh the page or contact support if this issue persists.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!userData.id) return;
    
    setIsSaving(true);
    try {
      console.log('💾 Saving profile data:', formData);
      
      // Update user_profiles table instead of users table
      const { error } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: userData.id,
          name: formData.name,
          phone: formData.phone,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userData.id);

      if (error) {
        console.error("❌ Profile update error:", error);
        throw error;
      }

      console.log('✅ Profile updated successfully');
      toast.success("Profile updated successfully");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = () => {
    setFormData({
      name: userData.name || '',
      phone: userData.phone || ''
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: userData.name || '',
      phone: userData.phone || ''
    });
  };

  return (
    <Card>
      <CardHeader className={`flex flex-row items-center justify-between space-y-0 ${isMobile ? "pb-3" : "pb-4"}`}>
        <CardTitle className={isMobile ? "text-lg" : ""}>{t("personal_information")}</CardTitle>
        {!isEditing && (
          <Button 
            variant="outline" 
            size={isMobile ? "sm" : "sm"}
            onClick={handleEdit}
          >
            <Edit className="h-4 w-4 mr-2" />
            {t("edit")}
          </Button>
        )}
      </CardHeader>
      <CardContent className={`space-y-${isMobile ? "3" : "4"}`}>
        <div className="grid gap-2">
          <Label htmlFor="userId" className={isMobile ? "text-sm" : ""}>{t("user_id")}</Label>
          <Input id="userId" value={userData.id || '-'} readOnly disabled className={isMobile ? "h-9" : ""} />
        </div>
        
        <div className="grid gap-2">
          <Label htmlFor="name" className={isMobile ? "text-sm" : ""}>{t("name")}</Label>
          <Input 
            id="name"
            name="name"
            value={isEditing ? formData.name : (userData.name || '-')}
            onChange={handleInputChange}
            readOnly={!isEditing}
            disabled={!isEditing}
            className={isMobile ? "h-9" : ""}
          />
        </div>
        
        <div className="grid gap-2">
          <Label htmlFor="email" className={isMobile ? "text-sm" : ""}>{t("email")}</Label>
          <Input id="email" value={userData.email || '-'} readOnly disabled className={isMobile ? "h-9" : ""} />
        </div>
        
        <div className="grid gap-2">
          <Label htmlFor="phone" className={isMobile ? "text-sm" : ""}>{t("phone")}</Label>
          <Input 
            id="phone"
            name="phone"
            value={isEditing ? formData.phone : (userData.phone || '-')}
            onChange={handleInputChange}
            readOnly={!isEditing}
            disabled={!isEditing}
            className={isMobile ? "h-9" : ""}
          />
        </div>

        {isEditing && (
          <div className="flex gap-2 justify-end pt-4">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              size={isMobile ? "sm" : "default"}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              size={isMobile ? "sm" : "default"}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("saving")}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {t("save_changes")}
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
